import type { ApiTypingsLookup } from "@/api";

import { registerPreviewHandler, type PreviewHandler } from "../registry";
import {
  handleGetAiChatConversation,
  handleGetInsightCards,
  handleListAiChatConversations,
  handleSaveAiChatConversation,
} from "./ai-chat";
import {
  handleAnalyticsFunnelQuery,
  handleFunnelQuery,
  handleListFunnels,
} from "./funnels";
import { handleListActiveDestinations } from "./destinations";
import {
  handleGetDynamicEvents,
  handleGetEventDefsEnriched,
} from "./event-defs";
import {
  handleGetRecommendationCards,
  handleSubmitRecommendationCardFeedback,
} from "./insights";
import {
  handleGetShortLink,
  handleGetShortLinkClicks,
  handleGetShortLinkDomains,
  handleGetShortLinkMetrics,
  handleGetShortLinkTags,
  handleGetShortLinkTimeSeries,
  handleGetShortLinks,
  handlePreviewShortLinkQr,
} from "./short-links";
import {
  handleGetSurvey,
  handleGetSurveyResponses,
  handleGetSurveyResults,
  handleGetSurveyTimeseries,
  handleGetSurveys,
} from "./surveys";
import {
  handleAnalyticsEventQuery,
  handleAnalyticsTopConversions,
  handleAnalyticsTopEvents,
} from "./web-analytics";

// ApiTypingsLookup declares survey responses as parsed FE shapes (e.g.
// Survey[]), but front/src/surveys/api.ts deserializes the wire shape
// ({ surveys: BackendSurvey[] }). Our handlers return the wire shape so
// the deserializer keeps working; this cast bridges the disconnect
// without forcing a refactor of api/surveys.ts.
function asPreviewHandler<K extends keyof ApiTypingsLookup>(
  fn: (params: ApiTypingsLookup[K]["request"]) => unknown,
): PreviewHandler<K> {
  return fn as PreviewHandler<K>;
}

export function registerAllPreviewModeHandlers(): void {
  registerPreviewHandler("list_funnels", handleListFunnels);
  registerPreviewHandler("analytics_funnel_query", handleAnalyticsFunnelQuery);
  registerPreviewHandler("funnel_query", handleFunnelQuery);
  registerPreviewHandler("get_event_defs_enriched", handleGetEventDefsEnriched);
  registerPreviewHandler("get_dynamic_events", handleGetDynamicEvents);
  registerPreviewHandler(
    "list_active_destinations",
    handleListActiveDestinations,
  );

  registerPreviewHandler("analytics_event_query", handleAnalyticsEventQuery);
  registerPreviewHandler(
    "analytics_top_conversions",
    handleAnalyticsTopConversions,
  );
  registerPreviewHandler("analytics_top_events", handleAnalyticsTopEvents);

  registerPreviewHandler(
    "get_surveys",
    asPreviewHandler<"get_surveys">(handleGetSurveys),
  );
  registerPreviewHandler(
    "get_survey",
    asPreviewHandler<"get_survey">(handleGetSurvey),
  );
  registerPreviewHandler(
    "get_survey_results",
    asPreviewHandler<"get_survey_results">(handleGetSurveyResults),
  );
  registerPreviewHandler(
    "get_survey_timeseries",
    asPreviewHandler<"get_survey_timeseries">(handleGetSurveyTimeseries),
  );
  registerPreviewHandler(
    "get_survey_responses",
    asPreviewHandler<"get_survey_responses">(handleGetSurveyResponses),
  );

  registerPreviewHandler("get_short_links", handleGetShortLinks);
  registerPreviewHandler("get_short_link", handleGetShortLink);
  registerPreviewHandler("get_short_link_tags", handleGetShortLinkTags);
  registerPreviewHandler("get_short_link_domains", handleGetShortLinkDomains);
  registerPreviewHandler("get_short_link_clicks", handleGetShortLinkClicks);
  registerPreviewHandler(
    "get_short_link_time_series",
    handleGetShortLinkTimeSeries,
  );
  registerPreviewHandler("get_short_link_metrics", handleGetShortLinkMetrics);
  registerPreviewHandler("preview_short_link_qr", handlePreviewShortLinkQr);

  registerPreviewHandler(
    "ai_chat_list_conversations",
    handleListAiChatConversations,
  );
  registerPreviewHandler(
    "ai_chat_get_conversation",
    handleGetAiChatConversation,
  );
  registerPreviewHandler(
    "ai_chat_save_conversation",
    handleSaveAiChatConversation,
  );
  registerPreviewHandler("ai_chat_get_insight_cards", handleGetInsightCards);
  registerPreviewHandler(
    "get_recommendation_cards_with_state",
    handleGetRecommendationCards,
  );
  registerPreviewHandler(
    "submit_recommendation_card_feedback",
    handleSubmitRecommendationCardFeedback,
  );
}
